import { FC, useEffect, useRef, useState, useCallback } from 'react'
import { AlphaSlider } from './AlphaSlider.js'
import { HueSlider } from './HueSlider.js'
import { RgbPicker } from './RgbPicker.js'
import { rgbToXY, xyToRgb, rgbToHue, rgbaToArr, hexToRGBA } from './utils.js'
import type { RGB } from './utils.js'
import { Style, styled } from 'inlines'
import { IconButton } from '../IconButton/index.js'
import {
  hexToHSL,
  hslToRGBA,
  rgbaToHex,
  rgbaToHsla,
  COLOR_FORMAT,
} from './utils.js'
import useEyeDropper from 'use-eye-dropper'
import { ColorSettings } from './ColorSettings.js'
import { Stack } from '../Stack/index.js'

type ColorPickerProps = {
  value?: string
  style?: Style
  onChange?: (color: string) => void
  colorFormat?: COLOR_FORMAT
  setColorFormat?: (colorFormat: COLOR_FORMAT) => void
}

export const ColorPicker: FC<ColorPickerProps> = ({
  value,
  style,
  onChange,
  colorFormat = 'HEX',
  setColorFormat,
}) => {
  const defaultValue =
    colorFormat === 'HEX'
      ? '#000000'
      : colorFormat === 'HSL'
        ? 'hsla(0deg, 0%, 0%, 1)'
        : 'rgba(0, 0, 0, 1)'

  const initialValue = value || defaultValue
  const valueRef = useRef(initialValue)

  const { open, isSupported } = useEyeDropper()

  //convert values to rgba string
  let newValue =
    colorFormat === 'HEX'
      ? hexToRGBA(initialValue)
      : colorFormat === 'HSL'
        ? hslToRGBA(initialValue)
        : initialValue

  const rgba = rgbaToArr(newValue)
  const [rgb, setRgb] = useState(rgba.slice(0, 3) as RGB)
  const [hue, setHue] = useState(rgbToHue(rgb))
  const [alpha, setAlpha] = useState(rgba[3])

  // convert value from rgba to wanted output format
  const colorValue =
    colorFormat === 'HEX'
      ? rgbaToHex(`rgba(${rgb.map(Math.round).join(',')},${alpha})`)
      : colorFormat === 'HSL'
        ? rgbaToHsla(`rgba(${rgb.map(Math.round).join(',')},${alpha})`)
        : `rgba(${rgb.map(Math.round).join(',')},${alpha})`

  useEffect(() => {
    if (value && value !== valueRef.current) {
      const updatedValue =
        colorFormat === 'HEX'
          ? hexToRGBA(value)
          : colorFormat === 'HSL'
            ? hslToRGBA(value)
            : value
      const newRgba = rgbaToArr(updatedValue)
      const rgb = newRgba.slice(0, 3) as RGB
      setRgb(rgb)
      setHue(rgbToHue(rgb))
      setAlpha(newRgba[3])
    }
  }, [value, colorFormat])

  useEffect(() => {
    if (onChange && colorValue !== value) {
      valueRef.current = colorValue
      onChange(colorValue)
    }
  }, [onChange, colorValue])

  const pickColor = useCallback(
    (colorFormat: string) => {
      // Using async/await (can be used as a promise as-well)
      const openPicker = async () => {
        try {
          const color = await open()
          // Handle the color formats in here as well.
          if (colorFormat === 'RGB') {
            onChange(hexToRGBA(color.sRGBHex))
          } else if (colorFormat === 'HSL') {
            onChange(hexToHSL(color.sRGBHex))
          } else {
            onChange(color.sRGBHex)
          }
        } catch (e) {
          console.error(e)
        }
      }
      openPicker()
    },
    [open],
  )

  return (
    <Stack gap={12} direction="column">
      <RgbPicker hue={hue} rgb={rgb} onChange={setRgb} />
      <Stack gap={8} direction="row" grow style={{ width: '100%' }}>
        <Stack gap={8} direction="column" grow style={{ width: '100%' }}>
          <HueSlider
            hue={hue}
            onChange={(newHue: [number, number, number]) => {
              const { x, y } = rgbToXY(rgb, hue)
              const newRgb = xyToRgb(x, y, newHue)
              setRgb(newRgb as [number, number, number])
              setHue(newHue)
            }}
          />
          <AlphaSlider rgb={rgb} alpha={alpha} onChange={setAlpha} />
        </Stack>
        {isSupported() && (
          <IconButton
            icon="color-picker"
            onClick={() => pickColor(colorFormat)}
          />
        )}
      </Stack>
      <ColorSettings
        colorFormat={colorFormat}
        setColorFormat={setColorFormat}
        colorValue={colorValue}
        onChange={onChange}
      />
    </Stack>
  )
}
